import React from 'react';
import { Button } from 'react-bootstrap';
import { DeviceType, BANNER_DIMENSIONS } from './types';
import UploadImageButton from './UploadImageButton';
import type { BannerService } from './FileProcessingUtils';

interface MediaDisplayProps {
  index: number;
  type: DeviceType;
  imageUrl?: string | undefined;
  title: string;
  onRemove: (index: number, type: DeviceType) => void;
  // Context props to pass to UploadImageButton
  banner?: BannerService;
  isUploading?: boolean;
  setIsUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
  setUploadedFiles?: React.Dispatch<React.SetStateAction<any[]>>;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({
  index,
  type,
  imageUrl,
  title,
  onRemove,
  banner,
  isUploading,
  setIsUploading,
  setError,
  setUploadedFiles,
}) => {
  // Use the formatted dimensions for display (with multiplication sign)
  const sizeMap = {
    [DeviceType.DESKTOP]: BANNER_DIMENSIONS.DESKTOP.replace('x', '×'),
    [DeviceType.TABLET]: BANNER_DIMENSIONS.TABLET.replace('x', '×'),
    [DeviceType.MOBILE]: BANNER_DIMENSIONS.MOBILE.replace('x', '×'),
  };

  // Proportional sizing - make desktop significantly larger than tablet
  let maxMediaHeight;

  switch (type) {
    case DeviceType.DESKTOP:
      maxMediaHeight = '300px'; // More reasonable height
      break;
    case DeviceType.TABLET:
      maxMediaHeight = '220px';
      break;
    case DeviceType.MOBILE:
      maxMediaHeight = '180px';
      break;
    default:
      maxMediaHeight = '220px';
  }

  // Create a dummy banner service if none is provided
  const dummyBanner: BannerService = {
    uploadFile: async () => null,
    removeUploadingFile: () => {},
    uploadingFiles: {},
  };

  // Check if the URL is a video file
  const isVideo = imageUrl?.toLowerCase().endsWith('.mp4') || false;

  return (
    <div className="text-center h-100 px-2">
      <h5 className="mb-2 text-muted">
        {type.charAt(0).toUpperCase() + type.slice(1)}
        <small className="ms-1 text-muted">({sizeMap[type]})</small>
      </h5>

      {imageUrl ? (
        <div>
          <Button variant="outline-secondary" size="sm" onClick={() => onRemove(index, type)} className="mb-2">
            Remove {type} {isVideo ? 'Video' : 'Image'}
          </Button>
          <div
            style={{
              height: 'auto',
              minHeight: '200px',
              position: 'relative',
              display: 'block',
            }}
          >
            {isVideo ? (
              <video
                src={imageUrl}
                className="img-fluid rounded"
                style={{
                  maxHeight: maxMediaHeight,
                  width: '100%',
                  objectFit: 'contain',
                  verticalAlign: 'top',
                }}
                controls
                muted
                preload="metadata"
              />
            ) : (
              <img
                src={imageUrl}
                alt={`${title} (${type})`}
                className="img-fluid rounded"
                style={{
                  maxHeight: maxMediaHeight,
                  width: '100%',
                  objectFit: 'contain',
                  verticalAlign: 'top',
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="mt-5">
          <UploadImageButton
            index={index}
            deviceType={type}
            banner={banner || dummyBanner}
            isUploading={isUploading || false}
            setIsUploading={setIsUploading || (() => {})}
            setError={setError || (() => {})}
            setUploadedFiles={setUploadedFiles || (() => {})}
          />
        </div>
      )}
    </div>
  );
};

export default MediaDisplay;
